import {
    type CSSProperties,
    useState
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    type DateTimePickerDateRange,
    type ITimeSelectorProps,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    formatDateTime,
    TimeSelector
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

type TimeSelectorSampleProps = Omit<ITimeSelectorProps, "selectMultipleObject" | "selectObject" | "pickerType" | "dateRange" | "date"> & {
    initialDateRange?: DateTimePickerDateRange;
    isFramed?: boolean;
    initialDate?: Date;
};

const NAME = "TimeSelector";

const USAGE_CODE = `const [time, setTime] = useState<Date>();

<TimeSelector
    selectMultipleObject={() => undefined}
    selectObject={setTime}
    pickerType="time"
    variant="single"
    date={time}
/>`;

const RANGE_CODE = `const [range, setRange] = useState<DateTimePickerDateRange>();

<TimeSelector
    multipleSelectMinimumRequireMS={30 * 60 * 1000}
    multipleSelectMaximumMS={10 * 60 * 60 * 1000}
    selectMultipleObject={setRange}
    selectObject={() => undefined}
    dateRange={range}
    pickerType="time"
    variant="range"
/>`;

const LIMITS_CODE = `<TimeSelector
    selectMultipleObject={() => undefined}
    maxDate={new Date(2026, 9, 26, 18, 0)}
    minDate={new Date(2026, 9, 26, 9, 0)}
    selectObject={setTime}
    pickerType="time"
    variant="single"
    date={time}
/>`;

const FRAME_STYLE: CSSProperties = {
    display: "inline-block",
    maxWidth: "100%"
};

const createTime = (hours: number, minutes: number, seconds = 0) => {
    const date = new Date();

    date.setHours(hours, minutes, seconds, 0);

    return date;
};

const MINUTE_MS = 60 * 1000;

const HOUR_MS = 60 * MINUTE_MS;

const MIN_GAP_MS = 30 * MINUTE_MS;

const MAX_GAP_MS = 10 * HOUR_MS;

const SAMPLE_TIME = createTime(14, 30);

const SAMPLE_RANGE = {
    start: createTime(9, 0),
    end: createTime(17, 30)
};

const WRONG_RANGE = {
    start: createTime(17, 30),
    end: createTime(9, 0)
};

const MIN_TIME = createTime(9, 0);

const MAX_TIME = createTime(18, 0);

const TimeSelectorSample = ({
    initialDateRange,
    isFramed = true,
    initialDate,
    ...props
}: TimeSelectorSampleProps) => {
    const [
        dateRange,
        setDateRange
    ] = useState<DateTimePickerDateRange | undefined>(initialDateRange);

    const [
        date,
        setDate
    ] = useState<Date | undefined>(initialDate);

    const renderSelector = () => <TimeSelector
        {...props}
        selectMultipleObject={setDateRange}
        selectObject={setDate}
        dateRange={dateRange}
        pickerType="time"
        date={date}
    />;

    if(!isFramed) {
        return renderSelector();
    }

    return <div
        style={FRAME_STYLE}
    >
        {renderSelector()}
    </div>;
};

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("timeSelectorPage.formatTitle"),
        id: "format"
    },
    {
        title: t("timeSelectorPage.rangeTitle"),
        id: "range"
    },
    {
        title: t("timeSelectorPage.limitsTitle"),
        id: "limits"
    },
    {
        title: t("timeSelectorPage.layoutTitle"),
        id: "layout"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const TimeSelectorPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const [
        time,
        setTime
    ] = useState<Date | undefined>(SAMPLE_TIME);

    const [
        workRange,
        setWorkRange
    ] = useState<DateTimePickerDateRange | undefined>(SAMPLE_RANGE);

    const formatTime = (value: Date) => formatDateTime({
        options: {
            minute: "2-digit",
            hourCycle: "h23",
            hour: "2-digit"
        },
        locale: activeLocale,
        date: value
    });

    const getTimeCaption = () => {
        if(!time) {
            return t("timeSelectorPage.emptyCaption");
        }

        return t("timeSelectorPage.selectedCaption", [
            formatTime(time)
        ]);
    };

    const getRangeCaption = () => {
        if(!workRange?.start || !workRange.end) {
            return t("timeSelectorPage.rangeEmptyCaption");
        }

        return t("timeSelectorPage.rangeCaption", [
            formatTime(workRange.start),
            formatTime(workRange.end)
        ]);
    };

    return <Article>
        <ComponentHeader
            lead={t("timeSelectorPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("timeSelectorPage.overviewText")}
            </Paragraph>
            <Preview
                caption={getTimeCaption()}
            >
                <div
                    style={FRAME_STYLE}
                >
                    <TimeSelector
                        selectMultipleObject={() => undefined}
                        selectObject={setTime}
                        pickerType="time"
                        variant="single"
                        date={time}
                    />
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("timeSelectorPage.formatTitle")}
            id="format"
        >
            <Paragraph>
                {t("timeSelectorPage.formatText")}
            </Paragraph>
            <Preview>
                <TimeSelectorSample
                    initialDate={SAMPLE_TIME}
                    variant="single"
                />
                <TimeSelectorSample
                    initialDate={createTime(14, 30, 45)}
                    isWorkWithSeconds={true}
                    variant="single"
                />
                <TimeSelectorSample
                    isWorkWith24HoursFormat={false}
                    initialDate={SAMPLE_TIME}
                    variant="single"
                />
            </Preview>
        </Section>
        <Section
            title={t("timeSelectorPage.rangeTitle")}
            id="range"
        >
            <Paragraph>
                {t("timeSelectorPage.rangeText")}
            </Paragraph>
            <Preview
                caption={getRangeCaption()}
            >
                <div
                    style={FRAME_STYLE}
                >
                    <TimeSelector
                        multipleSelectMinimumRequireMS={MIN_GAP_MS}
                        multipleSelectMaximumMS={MAX_GAP_MS}
                        selectMultipleObject={setWorkRange}
                        selectObject={() => undefined}
                        dateRange={workRange}
                        pickerType="time"
                        variant="range"
                    />
                </div>
                <TimeSelectorSample
                    initialDateRange={WRONG_RANGE}
                    variant="range"
                />
            </Preview>
            <CodeBlock
                code={RANGE_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("timeSelectorPage.limitsTitle")}
            id="limits"
        >
            <Paragraph>
                {t("timeSelectorPage.limitsText")}
            </Paragraph>
            <Preview>
                <TimeSelectorSample
                    initialDate={SAMPLE_TIME}
                    maxDate={MAX_TIME}
                    minDate={MIN_TIME}
                    variant="single"
                />
                <TimeSelectorSample
                    initialDate={createTime(8, 0)}
                    maxDate={MAX_TIME}
                    minDate={MIN_TIME}
                    variant="single"
                />
            </Preview>
            <CodeBlock
                code={LIMITS_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("timeSelectorPage.layoutTitle")}
            id="layout"
        >
            <Paragraph>
                {t("timeSelectorPage.layoutText")}
            </Paragraph>
            <Preview>
                <DemoFrame>
                    <View
                        style={{
                            padding: docsTokens.spaces.spacingMd
                        }}
                    >
                        <TimeSelectorSample
                            initialDate={SAMPLE_TIME}
                            isFramed={false}
                            variant="single"
                        />
                    </View>
                </DemoFrame>
                <DemoFrame>
                    <View
                        style={{
                            padding: docsTokens.spaces.spacingMd
                        }}
                    >
                        <TimeSelectorSample
                            initialDate={SAMPLE_TIME}
                            labelPosition="start"
                            isFramed={false}
                            variant="single"
                        />
                    </View>
                </DemoFrame>
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("timeSelectorPage.statesText")}
            </Paragraph>
            <Preview>
                <TimeSelectorSample
                    initialDate={SAMPLE_TIME}
                    isDisabled={true}
                    variant="single"
                />
                <TimeSelectorSample
                    initialDate={SAMPLE_TIME}
                    isReadOnly={true}
                    variant="single"
                />
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("timeSelectorPage.a11yGroup"),
                    t("timeSelectorPage.a11yField"),
                    t("timeSelectorPage.a11yInput"),
                    t("timeSelectorPage.a11yError"),
                    t("timeSelectorPage.a11yDayPeriod"),
                    t("timeSelectorPage.a11yState"),
                    t("timeSelectorPage.a11yDirection"),
                    t("timeSelectorPage.a11yMotion")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("timeSelectorPage.propPickerType"),
                        type: "\"date\" | \"time\" | \"date-time\"",
                        name: "pickerType"
                    },
                    {
                        description: t("timeSelectorPage.propVariant"),
                        type: "\"single\" | \"range\" | \"rrule\"",
                        name: "variant"
                    },
                    {
                        description: t("timeSelectorPage.propSelectObject"),
                        type: "(date?: Date) => void",
                        name: "selectObject"
                    },
                    {
                        description: t("timeSelectorPage.propSelectMultipleObject"),
                        type: "(dateRange?: DateTimePickerDateRange) => void",
                        name: "selectMultipleObject"
                    },
                    {
                        description: t("timeSelectorPage.propDate"),
                        name: "date",
                        type: "Date"
                    },
                    {
                        description: t("timeSelectorPage.propDateRange"),
                        type: "DateTimePickerDateRange",
                        name: "dateRange"
                    },
                    {
                        description: t("timeSelectorPage.propIsWorkWith24HoursFormat"),
                        name: "isWorkWith24HoursFormat",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("timeSelectorPage.propIsWorkWithSeconds"),
                        name: "isWorkWithSeconds",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("timeSelectorPage.propMinDate"),
                        name: "minDate",
                        type: "Date"
                    },
                    {
                        description: t("timeSelectorPage.propMaxDate"),
                        name: "maxDate",
                        type: "Date"
                    },
                    {
                        description: t("timeSelectorPage.propMultipleSelectMinimumRequireMS"),
                        name: "multipleSelectMinimumRequireMS",
                        type: "number"
                    },
                    {
                        description: t("timeSelectorPage.propMultipleSelectMaximumMS"),
                        name: "multipleSelectMaximumMS",
                        type: "number"
                    },
                    {
                        description: t("timeSelectorPage.propLabelPosition"),
                        type: "\"top\" | \"start\"",
                        defaultValue: "\"top\"",
                        name: "labelPosition"
                    },
                    {
                        description: t("timeSelectorPage.propIsTimeSelectWorksWithCenter"),
                        name: "isTimeSelectWorksWithCenterWhenTimePicker",
                        type: "boolean"
                    },
                    {
                        description: t("timeSelectorPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("timeSelectorPage.propIsReadOnly"),
                        defaultValue: "false",
                        name: "isReadOnly",
                        type: "boolean"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("timeSelectorPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("timeSelectorPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("timeSelectorPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default TimeSelectorPage;
